import React, { useEffect, useState } from "react";
import { Trash2, Paperclip, Mail, Download } from "lucide-react";
import { toast } from "sonner";
import api, { API, downloadCsv } from "@/lib/api";
import { Section, Btn, Select, cardCls, StatusPill, EmptyState } from "@/components/admin/ui";
import Modal from "@/components/admin/Modal";
import { useAuth } from "@/context/AuthContext";

const CATS = ["", "General", "Partnership", "Supplier", "Investment/Acquisition Opportunity", "Media", "Careers", "Venture Enquiry"];
const STATUSES = ["New", "Contacted", "Follow-up", "Won", "Lost"];

export default function Enquiries() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ceo" || user?.role === "admin";
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [view, setView] = useState(null);

  const load = () => api.get("/admin/enquiries", { params: cat ? { category: cat } : {} }).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, [cat]);

  const setStatus = async (e, status) => { await api.patch(`/admin/enquiries/${e.id}`, { status }); load(); if (view?.id === e.id) setView({ ...view, status }); };
  const remove = async (e) => { if (!window.confirm("Delete this enquiry?")) return; await api.delete(`/admin/enquiries/${e.id}`); setView(null); load(); toast.success("Deleted"); };

  return (
    <div data-testid="admin-enquiries">
      <Section title="Enquiries" subtitle="Every website submission — contact, partnership, supplier, opportunity, media, careers and venture enquiries."
        action={<Btn variant="outline" data-testid="enquiry-export" onClick={() => downloadCsv("enquiries", "enquiries.csv")}><Download size={15} /> Export CSV</Btn>}>
        <div className="mb-6 max-w-xs">
          <Select data-testid="enquiry-filter" value={cat} onChange={(e) => setCat(e.target.value)}>
            {CATS.map((c) => <option key={c || "all"} value={c}>{c || "All categories"}</option>)}
          </Select>
        </div>
        {items.length === 0 ? <EmptyState>No enquiries yet.</EmptyState> : (
          <div className={`${cardCls} divide-y divide-white/5`}>
            {items.map((e) => (
              <button key={e.id} data-testid={`enquiry-row-${e.id}`} onClick={() => setView(e)} className="w-full text-left flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-bd-ivory">{e.name}</span>
                    <StatusPill status={e.status} />
                    <span className="text-[0.65rem] uppercase tracking-wider text-bd-gold/70">{e.category}</span>
                  </div>
                  <p className="text-xs text-bd-ivory/40 truncate mt-1">{e.message}</p>
                </div>
                <span className="text-xs text-bd-ivory/30 whitespace-nowrap">{new Date(e.created_at).toLocaleDateString("en-GB")}</span>
              </button>
            ))}
          </div>
        )}
      </Section>

      <Modal open={!!view} onClose={() => setView(null)} title="Enquiry" testId="enquiry-modal">
        {view && (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-3"><StatusPill status={view.status} /><span className="text-[0.65rem] uppercase tracking-wider text-bd-gold/70">{view.category}</span></div>
            <Row k="Name" v={view.name} />
            <Row k="Email" v={<a href={`mailto:${view.email}`} className="text-bd-gold hover:underline inline-flex items-center gap-1"><Mail size={13} />{view.email}</a>} />
            {view.company && <Row k="Company" v={view.company} />}
            {view.position && <Row k="Position" v={view.position} />}
            {view.phone && <Row k="Phone" v={view.phone} />}
            {view.partnership_type && <Row k="Partnership" v={view.partnership_type} />}
            {view.venture_of_interest && <Row k="Venture" v={view.venture_of_interest} />}
            <div>
              <p className="text-xs uppercase tracking-wider text-bd-ivory/40 mb-1">Message</p>
              <p className="text-bd-ivory/80 whitespace-pre-line leading-relaxed">{view.message}</p>
            </div>
            {view.attachment_id && (
              <a href={`${API}/files/${view.attachment_id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-bd-gold hover:text-bd-gold2"><Paperclip size={14} /> View attachment</a>
            )}
            <div className="pt-4 border-t border-white/10">
              <p className="text-xs uppercase tracking-wider text-bd-ivory/40 mb-2">Update status</p>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button key={s} onClick={() => setStatus(view, s)} className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${view.status === s ? "border-bd-gold text-bd-gold" : "border-white/15 text-bd-ivory/50 hover:text-bd-ivory"}`}>{s}</button>
                ))}
              </div>
            </div>
            {isAdmin && (
              <div className="flex justify-end pt-2">
                <Btn variant="danger" onClick={() => remove(view)}><Trash2 size={14} /> Delete</Btn>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-4">
      <span className="w-28 flex-shrink-0 text-xs uppercase tracking-wider text-bd-ivory/40 pt-0.5">{k}</span>
      <span className="text-bd-ivory/80">{v}</span>
    </div>
  );
}
